// urlValidation.js — shared URL safety/validation for Builder block settings.
//
// Nemesis batch 5 (blocks) — one place for the URL rules the block editors were
// each missing (hyperlink fields, video/audio embed sources, linked-wheel id
// extraction). Every rule here is a pure function so it is unit-testable and
// cannot drift per-block.
//
// Security intent: block editors persist creator input that is later rendered on
// PUBLIC viewer pages (iframe src, anchor href). Un-vetted input is a stored-XSS
// vector, so these helpers reject dangerous schemes (javascript:/data:/…) and,
// for embeds, constrain to an allow-list of known providers.

const DANGEROUS_SCHEME_RE = /^\s*(javascript|data|vbscript|file):/i;

// True for the schemes that can execute / smuggle script when placed in an
// href or iframe src. Anything matching must never be persisted or navigated to.
export function hasDangerousScheme(url = "") {
  return DANGEROUS_SCHEME_RE.test(String(url));
}

// Parse `input` into a http(s) URL object, or null if it cannot be one.
// Scheme-less input (example.com/x) is accepted by trying an https:// prefix,
// mirroring the app's goToUrl() behaviour. Dangerous schemes always => null.
export function toHttpUrl(input = "") {
  const raw = String(input).trim();
  if (!raw || hasDangerousScheme(raw)) return null;
  let candidate = raw;
  if (!/^https?:\/\//i.test(candidate)) candidate = `https://${candidate}`;
  try {
    const u = new URL(candidate);
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    // Require a dotted host (or localhost) so bare junk ("abc") is rejected.
    if (!/\./.test(u.hostname) && u.hostname !== "localhost") return null;
    return u;
  } catch {
    return null;
  }
}

// A normalized, safe href for an anchor/button link — or null if not safe.
export function normalizeHttpUrl(input) {
  const u = toHttpUrl(input);
  return u ? u.href : null;
}

export function isSafeHttpUrl(input) {
  return toHttpUrl(input) !== null;
}

function hostMatches(host, allow) {
  const h = String(host || "").toLowerCase().replace(/^www\./, "");
  return allow.some((d) => h === d || h.endsWith(`.${d}`));
}

// --- Video embed allow-list -------------------------------------------------
export const VIDEO_EMBED_HOSTS = [
  "youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
  "vimeo.com",
  "player.vimeo.com",
  "instagram.com",
  "dailymotion.com",
  "dai.ly",
  "loom.com",
  "wistia.com",
  "wistia.net",
  "tiktok.com",
  "facebook.com",
  "fb.watch",
  "streamable.com",
];

export function isAllowedVideoEmbedUrl(input) {
  const u = toHttpUrl(input);
  return !!u && hostMatches(u.hostname, VIDEO_EMBED_HOSTS);
}

// --- Audio embed allow-list (matches EmbedCode's Spotify/SoundCloud support) -
export const AUDIO_EMBED_HOSTS = [
  "spotify.com",
  "open.spotify.com",
  "soundcloud.com",
  "w.soundcloud.com",
  "api.soundcloud.com",
];

export function isAllowedAudioEmbedSrc(input) {
  const u = toHttpUrl(input);
  return !!u && hostMatches(u.hostname, AUDIO_EMBED_HOSTS);
}

// --- Linked-wheel id extraction --------------------------------------------
// Robust against query strings, trailing slashes, `/null` suffixes, and both
// /wheel/:id and /builder/:id shapes. Accepts a bare numeric id directly.
export function extractLinkedWheelId(input = "") {
  const raw = String(input || "").trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return raw;
  const u = toHttpUrl(raw);
  const path = u ? u.pathname : raw;
  const m = path.match(/(?:wheel|builder)\/(\d+)/i);
  return m ? m[1] : null;
}

export default {
  hasDangerousScheme,
  toHttpUrl,
  normalizeHttpUrl,
  isSafeHttpUrl,
  isAllowedVideoEmbedUrl,
  isAllowedAudioEmbedSrc,
  extractLinkedWheelId,
  VIDEO_EMBED_HOSTS,
  AUDIO_EMBED_HOSTS,
};
